import { loadStripe } from "@stripe/stripe-js"

import getAppConfig from "~/utils/getAppConfig"

import type SavedPaymentMethod from "~/types/SavedPaymentMethod"
import type { PayablePaymentResource } from "~/types/PayablePaymentResource"
import type PaymentItem from "~/types/PaymentItem"

export const MANUAL_METHODS = [
  { value: "cash", label: "Cash" },
  { value: "check", label: "Check" },
  { value: "bank_transfer", label: "Bank Transfer" },
  { value: "paypal", label: "PayPal" },
  { value: "other", label: "Other" },
] as const

export const payableLabels: Record<PayablePaymentResource["payableType"], string> = {
  booking: "Booking",
  deal: "Deal",
  sponsorship: "Sponsorship",
}

export const formatSavedCard = (method?: SavedPaymentMethod | null) => {
  if (!method) return null

  return (
    method.displayName ||
    [
      method.brand,
      method.last4 ? `•••• ${method.last4}` : null,
      method.expMonth && method.expYear
        ? `exp ${String(method.expMonth).padStart(2, "0")}/${method.expYear}`
        : null,
    ]
      .filter(Boolean)
      .join(" ")
  )
}

export const confirmRequiredAction = async (
  clientSecret: string,
  stripeAccount?: string | null
) => {
  const { stripePublishableKey } = getAppConfig()

  if (!stripePublishableKey) {
    throw new Error("Stripe publishable key is missing.")
  }

  const stripe = await loadStripe(stripePublishableKey, stripeAccount ? { stripeAccount } : {})
  if (!stripe) {
    throw new Error("Failed to initialize Stripe.")
  }

  const result = await stripe.confirmCardPayment(clientSecret)

  if (result.error) {
    throw new Error(result.error.message || "Authentication failed.")
  }

  if (result.paymentIntent?.status !== "succeeded") {
    throw new Error("Payment was not completed.")
  }
}

export const buildEmptyPaymentItem = (currencyIso: string): PaymentItem => ({
  description: "",
  quantity: 1,
  amountCents: 0,
  currencyIso,
  _destroy: false,
})
